/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { poService, supplierService } from '../services/api'
import { useAuth } from '../context/AuthContext'
import POLineItemsTable from '../components/POLineItemsTable'

function PODetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isPanelok, isPanelokAdmin } = useAuth()
  const [po, setPO] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [columns, setColumns] = useState([])

  useEffect(() => {
    loadPO()
  }, [id])

  const loadPO = async () => {
    try {
      const response = await poService.getById(id)
      setPO(response)

      // Load supplier-specific column config
      try {
        const colRes = await supplierService.getColumns(response.supplier_number)
        if (colRes.columns) setColumns(colRes.columns)
      } catch { /* use default columns */ }
    } catch (err) {
      setError('Failed to load purchase order')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (newStatus) => {
    setUpdating(true)
    try {
      await poService.updateStatus(id, newStatus)
      loadPO()
    } catch (err) {
      setError(err.message || 'Failed to update status')
    } finally {
      setUpdating(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this PO?')) return

    try {
      await poService.delete(id)
      navigate('/purchase-orders', { state: { message: 'Purchase order deleted' } })
    } catch (err) {
      setError(err.message || 'Failed to delete')
    }
  }

  const getStatusBadge = (status) => {
    const s = status?.toUpperCase() || 'DRAFT'
    const base = {
      padding: '6px 14px',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: '500',
      cursor: 'default',
      display: 'inline-block'
    }
    const styles = {
      DRAFT: { ...base, backgroundColor: '#fef3c7', color: '#92400e' },
      ISSUED: { ...base, backgroundColor: '#d1fae5', color: '#065f46' },
      ARCHIVED: { ...base, backgroundColor: '#e5e7eb', color: '#6b7280' }
    }
    const displayText = s === 'ISSUED' ? 'Issued' : (s === 'ARCHIVED' ? 'Archived' : 'Draft')
    return <span style={styles[s] || styles.DRAFT}>{displayText}</span>
  }

  const handleIssue = async () => {
    if (!window.confirm('Issue this PO? This will lock it and generate a PDF.')) return
    await handleStatusChange('ISSUED')
  }

  const handleArchive = async () => {
    if (!window.confirm('Archive this PO?')) return
    await handleStatusChange('ARCHIVED')
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
        <div className="spinner" style={{ width: 40, height: 40 }}></div>
      </div>
    )
  }

  if (!po) {
    return (
      <div className="card">
        <div className="alert alert-danger">Purchase order not found</div>
        <Link to="/purchase-orders" className="btn btn-primary">Back to List</Link>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div>
          <Link to="/purchase-orders" style={{ color: '#666', textDecoration: 'none' }}>
            ← Back to Purchase Orders
          </Link>
          <h1 style={{ marginTop: 10 }}>{po.po_number}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowPdf(!showPdf)} className="btn btn-primary">
            {showPdf ? 'Hide PDF' : 'View PDF'}
          </button>
          <button onClick={() => window.open(poService.getPdfUrl(po.id), '_blank')} className="btn btn-outline">
            Download PDF
          </button>
          {po.status?.toUpperCase() === 'DRAFT' && isPanelok() && (
            <>
              <Link to={`/manual-po/${po.id}`} className="btn btn-secondary">Edit</Link>
              <button onClick={handleDelete} className="btn btn-danger">Delete</button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showPdf && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h2>PDF Preview</h2>
            <button onClick={() => setShowPdf(false)} className="btn btn-outline">Close</button>
          </div>
          <iframe src={poService.getPdfUrl(po.id)} className="pdf-viewer" title={`PDF - ${po.po_number}`} />
        </div>
      )}

      {po.supplierChanged && (
        <div className="alert alert-warning">
          <strong>Note:</strong> Supplier mapping has changed. This PO was created for "{po.supplier_name_snapshot}"
          but supplier #{po.supplier_number} now maps to "{po.currentSupplierDetails?.name}".
        </div>
      )}

      {/* Header Info */}
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Status</div>
            <div style={{ marginTop: 5 }}>{getStatusBadge(po.status)}</div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Supplier</div>
            <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 30, height: 30, background: '#1a5f7a', color: 'white',
                borderRadius: '50%', fontSize: '0.9rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600
              }}>
                {po.supplier_number}
              </span>
              <strong>{po.supplier_name_snapshot}</strong>
            </div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Job Number</div>
            <div style={{ marginTop: 5, fontWeight: 500 }}>{po.job_number || '-'}</div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Job Name</div>
            <div style={{ marginTop: 5 }}>{po.job_name || '-'}</div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Delivery</div>
            <div style={{ marginTop: 5 }}>{po.delivery_location}</div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Total Amount</div>
            <div style={{ marginTop: 5, fontWeight: 600, fontSize: '1.2rem', color: '#1a5f7a' }}>
              ${parseFloat(po.total_amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Source</div>
            <div style={{ marginTop: 5 }}>
              <span className={`badge ${po.source_type === 'CSV_IMPORT' ? 'badge-info' : 'badge-draft'}`}>
                {po.source_type === 'CSV_IMPORT' ? 'CSV Import' : 'Manual'}
              </span>
            </div>
          </div>
          <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Created</div>
            <div style={{ marginTop: 5 }}>{new Date(po.created_at).toLocaleString()}</div>
          </div>
          {po.job_reference && <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Job Reference</div>
            <div style={{ marginTop: 5, fontWeight: 500 }}>{po.job_reference}</div>
          </div>}
          {po.drawing_number && <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Drawing #</div>
            <div style={{ marginTop: 5 }}>{po.drawing_number}</div>
          </div>}
          {po.consolidation_number && <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Consolidation #</div>
            <div style={{ marginTop: 5 }}>{po.consolidation_number}</div>
          </div>}
          {po.delivery_date && <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Delivery Date</div>
            <div style={{ marginTop: 5 }}>{new Date(po.delivery_date).toLocaleDateString()}</div>
          </div>}
          {po.customer_name && <div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Customer</div>
            <div style={{ marginTop: 5 }}>{po.customer_name}</div>
          </div>}
        </div>
      </div>

      {/* Status Actions */}
      {isPanelok() && po.status?.toUpperCase() !== 'ARCHIVED' && (
        <div className="card">
          <div className="card-header"><h2>Actions</h2></div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {po.status?.toUpperCase() === 'DRAFT' && (
              <button onClick={handleIssue} className="btn" style={{ backgroundColor: '#10b981', color: 'white' }} disabled={updating}>
                {updating ? <span className="spinner"></span> : 'Issue PO'}
              </button>
            )}
            <button onClick={handleArchive} className="btn" style={{ backgroundColor: '#6b7280', color: 'white' }} disabled={updating}>
              {updating ? <span className="spinner"></span> : 'Archive'}
            </button>
          </div>
        </div>
      )}

      <POLineItemsTable lineItems={po.lineItems} totalAmount={po.total_amount} columns={columns} />
    </div>
  )
}

export default PODetail
